import React, { forwardRef, JSX, useEffect, useRef, useState } from 'react';
import { SxProps, useMediaQuery, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { CandidatePicker } from 'components/ui/CandidatePicker';
import { BackstoryTextFieldRef } from 'components/BackstoryTextField';
import { ConversationHandle } from 'components/Conversation';
import { useAuth } from 'hooks/AuthContext';
import { useAppState, useSelectedCandidate, useSelectedJob } from 'hooks/GlobalContext';
import {
  CandidateQuestion,
  ChatMessage,
  ChatMessageError,
  ChatMessageStatus,
  ChatMessageStreaming,
  ChatMessageUser,
  ChatSession,
  Resume,
} from 'types/types';
import { CandidateChatLayout } from './candidate-chat/CandidateChatLayout';
import {
  buildWelcomeMessage,
  defaultMessage,
  emptyMetadata,
  possessive,
} from './candidate-chat/chatDefaults';

interface CandidateChatPageProps {
  sx?: SxProps;
}

const CandidateChatPage = forwardRef<ConversationHandle, CandidateChatPageProps>(
  (props: CandidateChatPageProps, ref): JSX.Element => {
    const { resumeId } = useParams<{ resumeId?: string }>();
    const { selectedJob, setSelectedJob } = useSelectedJob();
    const { selectedCandidate, setSelectedCandidate } = useSelectedCandidate();
    const { apiClient } = useAuth();
    const { setSnack } = useAppState();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [resume, setResume] = useState<Resume | null>(null);
    const [processingMessage, setProcessingMessage] = useState<
      ChatMessageStatus | ChatMessageError | null
    >(null);
    const [streamingMessage, setStreamingMessage] = useState<ChatMessage | null>(null);
    const [chatSession, setChatSession] = useState<ChatSession | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [streaming, setStreaming] = useState(false);
    const [tabValue, setTabValue] = useState('chat');
    const backstoryTextRef = useRef<BackstoryTextFieldRef>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!resumeId || resume) return;
      apiClient
        .getResume(resumeId)
        .then(loadedResume => {
          setResume(loadedResume);
          if (loadedResume.candidate && loadedResume.candidate !== selectedCandidate) {
            setSelectedCandidate(loadedResume.candidate);
          }
          if (loadedResume.job && loadedResume.job !== selectedJob) {
            setSelectedJob(loadedResume.job);
          }
        })
        .catch(() => setSnack('Failed to load resume', 'error'));
    }, [
      resumeId,
      resume,
      apiClient,
      setSnack,
      selectedCandidate,
      selectedJob,
      setSelectedCandidate,
      setSelectedJob,
    ]);

    useEffect(() => {
      if (!selectedCandidate) return;
      setLoading(true);
      apiClient
        .getOrCreateChatSession(
          selectedCandidate,
          resumeId
            ? `Backstory Resume chat ${resumeId}`
            : `Backstory Chat with ${selectedCandidate.fullName}`,
          resumeId ? 'resume_chat' : 'candidate_chat'
        )
        .then(session => setChatSession(session))
        .catch(() => setSnack('Unable to load chat session', 'error'))
        .finally(() => setLoading(false));
    }, [selectedCandidate, apiClient, setSnack, resumeId]);

    useEffect(() => {
      if (!chatSession?.id) return;
      apiClient
        .getChatMessages(chatSession.id)
        .then(result => {
          setMessages(result.data);
          setProcessingMessage(null);
          setStreamingMessage(null);
        })
        .catch(error => console.error('Failed to load messages:', error));
    }, [chatSession, apiClient]);

    useEffect(() => {
      if (messages.length === 0 && processingMessage === null && streamingMessage === null) return;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, processingMessage, streamingMessage]);

    if (!selectedCandidate) {
      return <CandidatePicker />;
    }

    const onDelete = async (session: ChatSession): Promise<void> => {
      if (!session.id) return;
      try {
        await apiClient.resetChatSession(session.id);
        setMessages([]);
        setSnack('Session reset succeeded', 'success');
      } catch {
        setSnack('Failed to delete session', 'error');
      }
    };

    const sendMessage = async (message: string): Promise<void> => {
      if (!message.trim() || !chatSession?.id || streaming) return;
      const chatMessage = buildUserMessage(message, chatSession.id, resume);
      setStreaming(true);
      setProcessingMessage({
        ...defaultMessage,
        status: 'status',
        activity: 'info',
        content: `Establishing connection with ${selectedCandidate.firstName}'s chat session.`,
      });
      setMessages(prev => [
        ...prev.filter(m => m.id !== chatMessage.id),
        chatMessage as ChatMessage,
      ]);
      apiClient.sendMessageStream(chatMessage, {
        onMessage: msg => {
          setMessages(prev => [...prev.filter(m => m.id !== msg.id), msg]);
          setStreamingMessage(null);
          setProcessingMessage(null);
        },
        onError: error => {
          setSnack(errorMessage(error), 'error');
          setStreaming(false);
        },
        onStreaming: (chunk: ChatMessageStreaming) => {
          setProcessingMessage(null);
          setStreamingMessage({ ...chunk, role: 'assistant', metadata: emptyMetadata });
        },
        onStatus: status => setProcessingMessage(status),
        onComplete: () => {
          setStreamingMessage(null);
          setProcessingMessage(null);
          setStreaming(false);
        },
      });
    };

    const defaultQuestions = [
      `How well does ${possessive(
        selectedCandidate.firstName
      )} resume align with the job description?`,
      `Should ${selectedCandidate.firstName} be hired for the ${resume?.job?.title} position?`,
    ];

    return (
      <CandidateChatLayout
        sx={props.sx}
        forwardedRef={ref}
        isMobile={isMobile}
        tabValue={tabValue}
        onTabChange={setTabValue}
        resume={resume}
        selectedCandidate={selectedCandidate}
        chatSession={chatSession}
        messages={messages}
        processingMessage={processingMessage}
        streamingMessage={streamingMessage}
        streaming={streaming}
        loading={loading}
        defaultQuestions={defaultQuestions}
        welcomeMessage={buildWelcomeMessage(isMobile, chatSession?.id, selectedCandidate, resume)}
        messagesEndRef={messagesEndRef}
        backstoryTextRef={backstoryTextRef}
        onDelete={onDelete}
        onSendMessage={sendMessage}
        onSubmitQuestion={(question: CandidateQuestion): void => {
          sendMessage(question.question);
        }}
      />
    );
  }
);

function buildUserMessage(
  message: string,
  sessionId: string,
  resume: Resume | null
): ChatMessageUser {
  return {
    sessionId,
    role: 'user',
    content: message,
    status: 'done',
    type: 'text',
    timestamp: new Date(),
    extraContext: {
      candidateId: resume?.candidate?.id,
      jobId: resume?.job?.id,
      resumeId: resume?.id,
    },
  };
}

function errorMessage(error: string | ChatMessageError): string {
  if (typeof error === 'object' && error !== null && 'content' in error) {
    return error.content || 'The AI service is unavailable. Please retry later.';
  }
  if (typeof error === 'string') return `Error: ${error}`;
  return `An unknown error occurred: ${JSON.stringify(error)}`;
}

CandidateChatPage.displayName = 'CandidateChatPage';
export { CandidateChatPage };
